import Block from '@/components/Block'
import Button from '@/components/Button'
import EmptyBlock from '@/components/EmptyBlock'
import GsuLoader from '@/components/GsuLoader'
import Layout, { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { rbp } from '@/constants/measured-scope'
import { useFeatureToggle } from '@/context/featureToggles'
import { useUI } from '@/context/ui'
import { PageInfo, ScheduleDaySection } from '@/shared/api-generated-types'
import ClassCard from '@/widgets/ClassCard'
import ListViewClass from '@/widgets/ListViewClass'
import { useMeasure, useMediaQuery } from '@react-hookz/web'
import dayjs from 'dayjs'
import throttle from 'lodash/throttle'
import uniqBy from 'lodash/uniqBy'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SectionIntersectionObserver } from './SectionIntersectionObserver'
import * as styles from './style'

export interface Props {
  items: ScheduleDaySection[]
  className?: string
  onLoadMore?: (idx: number, page: number) => void
  pageInfo?: Partial<PageInfo>
  fetching?: boolean
  // Mostly used to indicate core data criteria has changed and to purge the old render results
  distinction?: string
}

const ScheduleBlock = ({
  items: daySections,
  className = '',
  pageInfo,
  onLoadMore: _onLoadMore,
  fetching,
  distinction,
}: Props): JSX.Element => {
  const isNewHorizontalClassCard = useFeatureToggle('exp_horizontal_class_card')
  const isDesktopScreen = useMediaQuery(`only screen and (min-width : ${rbp.xxl})`, { initializeWithValue: false })
  // An accumulative set of data blended with each new page request
  const [accumulatedDaySections, setAccumulatedDaySections] = useState<ScheduleDaySection[]>([])
  const lastPopulatedIndex = useRef<number>(0)
  const { state, dispatch } = useUI()

  //@ts-ignore
  const [measurements, ref] = useMeasure<HTMLDivElement>()
  const isWide = useMemo(() => measurements && measurements?.width >= 520, [measurements])

  // Pagination requirements
  const onLoadMore = _onLoadMore ? throttle(_onLoadMore, 2500) : undefined
  const isLoadMoreAvailable = onLoadMore && pageInfo?.hasNextPage
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const [loadMoreIntersectionObserver, setLoadMoreIntersectionObserver] = useState<IntersectionObserverEntry>()

  useEffect(() => {
    if (state.schedulePage.reset) {
      setTimeout(() => {
        // We set a half second delayed scroll to top,
        // to ensure when the user receives the data,
        // They view it from the top of the list.
        if (typeof window !== 'undefined') {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
          })
        }
      }, 500)
      setAccumulatedDaySections([])
      dispatch({ type: 'SET_ACTIVE_SCHEDULE_DATE', payload: { ...state.schedulePage, reset: false } })
    }
  }, [state])

  // Wipe existing day sections and allow data to be re-calculated since the
  // filter has changed, we can't be certain that the data we have is still valid
  useEffect(() => {
    setAccumulatedDaySections([])
  }, [distinction])

  /**
   * getSections processes ScheduleDaySections as React nodes
   * for the page rendering
   */
  const scheduleChunks = useMemo(() => {
    if (isWide === undefined) return <GsuLoader consumeSpace />
    return accumulatedDaySections.map(({ date, items: cards }) => {
      const sectionId = dayjs(date).format('YYYY-MM-DD')
      return (
        <div className="relative" key={date} data-testid="scheduleSection" id={sectionId}>
          <Stack x className={`${styles.tethered} mb-xxxs`} justifyContent="space-between">
            <Text
              tag="h2"
              size="md"
              weight="lg"
              responsiveSize={{ sm: 'lg', xxl: 'xl' }}
              className="fc-dark pb-sm fw-xb-gte-desktop pt-lg"
            >
              {/* TODO:   The date format must be:
                        USA -> MMMM DD, dddd
                        ROW -> MMMM, dddd DD
                        Needs Localization, basically.
            */}
              {dayjs(date).format('MMMM DD, dddd')}
            </Text>
            <SectionIntersectionObserver
              position="start"
              sectionId={`top-${sectionId}`}
              triggerValue={dayjs(date).format('YYYY-MM-DD')}
            />
          </Stack>

          <Layout spacing="sm" className="pb-lg">
            {typeof cards !== 'string' && cards?.length ? (
              <Stack y className={`${styles.cards} cardsWrapper ${isWide ? 'drop-shadow' : ''}`}>
                {cards.map((card) => {
                  return isNewHorizontalClassCard ? (
                    <ListViewClass {...card} key={card?.id} dateOnSide livenessType={card?.livenessType} />
                  ) : (
                    <ClassCard
                      isSchedule
                      fluid
                      wide={isWide}
                      key={card?.id}
                      feedItem
                      badgesTheme={isDesktopScreen ? 'FULL' : 'MINIMAL'}
                      {...card}
                    />
                  )
                })}
                <SectionIntersectionObserver
                  position="end"
                  sectionId={`bottom-${sectionId}`}
                  triggerValue={dayjs(date).format('YYYY-MM-DD')}
                />
              </Stack>
            ) : (
              <EmptyBlock>
                <Text tag="h3" size="sm" weight="sm" className="ta-c">
                  No classes available
                </Text>
              </EmptyBlock>
            )}
          </Layout>
        </div>
      )
    })
  }, [accumulatedDaySections, isWide])

  const loadMoreButton = useMemo(() => {
    if (!isLoadMoreAvailable) return

    // If session are being fetched from the server or the list of sessions is being fully refreshed, display full loading screen
    if (accumulatedDaySections.length === 0 || fetching) return <GsuLoader consumeSpace />

    // After sessions are fetched and rendered
    return (
      <div className="pt ta-c" ref={loadMoreRef}>
        <Button label="Load more" size="lg" onClick={onLoadMore} component="button" theme="primaryOutline" />
      </div>
    )
  }, [accumulatedDaySections, isLoadMoreAvailable, fetching])

  useEffect(() => {
    if (!loadMoreRef || !loadMoreRef.current) return
    const observer = new IntersectionObserver(
      ([entry]: IntersectionObserverEntry[]): void => {
        setLoadMoreIntersectionObserver(entry)
      },
      { threshold: 0, rootMargin: '0%' },
    )
    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [loadMoreRef?.current])

  useEffect(() => {
    if (!!loadMoreIntersectionObserver?.isIntersecting && !!pageInfo && isLoadMoreAvailable) {
      onLoadMore(0, pageInfo?.page ? pageInfo.page + 1 : 1)
    }
  }, [loadMoreIntersectionObserver?.isIntersecting])

  /**
   * As daySection data changes, we maintain the accumulated data set which
   * the client will be using to control rendering of the schedule.
   */
  useEffect(() => {
    // Do not process empty results in this manner
    if (!daySections?.length) return

    // If the backend returns sessions for days before the activeDate
    // This wee bit of code filters out those addl sessions.
    const arr = [...daySections]
    const chosenDate = dayjs(state.schedulePage.day || new Date())
      .subtract(1, 'day')
      .format('YYYY-MM-DD')
    const sectionDate = dayjs(daySections[0].date).format('YYYY-MM-DD')

    if (chosenDate === sectionDate) {
      arr.shift()
    }

    // Reference the first date inside response data
    const firstDateFromResponse = arr[0]?.date
    // The index of the last populated day section
    const prevLastIndex = accumulatedDaySections.length ? accumulatedDaySections.length - 1 : 0
    // Check the date of the last populated day section
    const lastPopulatedDate = accumulatedDaySections[prevLastIndex]?.date
    // Where the response data starts inside a date our client finished on
    // then we'll be populating more of that day data, thus 'partial day'
    const hasPartialDay = firstDateFromResponse === lastPopulatedDate
    // Update the last known populated index for chunk rendering
    lastPopulatedIndex.current = prevLastIndex

    if (hasPartialDay) {
      setAccumulatedDaySections((prev) => {
        // All day sections prior to the partial day that must be processed
        const oldSections = prev.length > 1 ? prev.slice(0, prev.length - 1) : []
        // Subset of day sections that is only the partial day
        const updates = prev.length > 1 ? prev.slice(prevLastIndex, prev.length) : [...prev]
        // Any day sections from the response that are not already accounted for on the client
        const newSections = arr.length > 1 ? arr.slice(1) : []
        try {
          // Blend items for the overlapped day, unique by session ID
          if (updates.length) updates[0].items = uniqBy([...updates[0]?.items, ...arr[0].items], 'id')
        } catch (error) {
          console.log('Issue blending partial day updates', { error })
        }

        return [...oldSections, ...updates, ...newSections]
      })
    } else {
      // No partial day to worry about, all new day sections
      setAccumulatedDaySections((prev) => {
        return uniqBy([...prev, ...arr], 'date')
      })
    }
  }, [daySections])

  return (
    <Block className={`${styles.root} ${className} p-0`}>
      <div ref={ref}></div>
      {!fetching && daySections.length === 0 ? (
        <EmptyBlock>
          <Text tag="h3" size="sm" weight="sm" className="ta-c p-sm">
            No classes available for chosen date
          </Text>
        </EmptyBlock>
      ) : (
        scheduleChunks
      )}
      {/* Note: the fetching state from useQuery hook is not always reliable, hence we render the load more button only when list of sessions is loaded */}
      {loadMoreButton}
    </Block>
  )
}
export default ScheduleBlock
